import { useState } from "react";
import { X, Loader2, Terminal } from "lucide-react";
import useRunAdbCommand from "../../hooks/useRunAdbCommand";

export default function RunAdbCommandDialog({ onClose }) {
  const {
    runAdbCommand,
    isLoading,
    error: hookError,
  } = useRunAdbCommand();

  const [stationId, setStationId] = useState("");
  const [gunId, setGunId] = useState("");
  const [command, setCommand] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    setError("");

    if (!stationId.trim()) {
      setError("Station ID is required");
      return;
    }

    if (!gunId.trim()) {
      setError("Gun ID is required");
      return;
    }

    if (!command.trim()) {
      setError("ADB command is required");
      return;
    }

    const res = await runAdbCommand({
      station_id: stationId,
      gun_id: gunId,
      command,
    });

    if (res.success) {
      onClose();
    } else {
      setError(res.error || "Failed to run ADB command");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white w-full max-w-md rounded-xl shadow-xl border border-gray-200">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <Terminal size={18} className="text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">
              Run ADB Command
            </h2>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition"
          >
            <X size={18} className="text-gray-700" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5 space-y-4">

          {/* Warning box */}
          <div className="border border-gray-300 bg-gray-50 rounded-lg p-3 text-sm text-gray-700">
            This command will be executed on the <b>tablet connected to the
            selected station & gun</b>. Use carefully.
          </div>

          {/* Station ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Station ID
            </label>
            <input
              value={stationId}
              onChange={(e) => setStationId(e.target.value)}
              placeholder="e.g. STATION_001"
              className="w-full px-3 py-2 border rounded-lg bg-gray-50
                         focus:outline-none focus:ring-2 focus:ring-black/10"
            />
          </div>

          {/* Gun ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gun ID
            </label>
            <input
              value={gunId}
              onChange={(e) => setGunId(e.target.value)}
              placeholder="e.g. yin / yang"
              className="w-full px-3 py-2 border rounded-lg bg-gray-50
                         focus:outline-none focus:ring-2 focus:ring-black/10"
            />
          </div>

          {/* Command */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ADB Command
            </label>
            <input
              value={command}
              onChange={(e) => setCommand(e.target.value)}
              placeholder="e.g. reboot"
              className="w-full px-3 py-2 border rounded-lg bg-gray-50
                         font-mono text-sm
                         focus:outline-none focus:ring-2 focus:ring-black/10"
            />
          </div>

          {(error || hookError) && (
            <p className="text-sm text-red-600">{error || hookError}</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300
                       text-gray-700 hover:bg-gray-100 transition"
          >
            Cancel
          </button>

          <button
            disabled={isLoading}
            onClick={handleSubmit}
            className="px-4 py-2 rounded-lg bg-black text-white
                       flex items-center gap-2 hover:bg-gray-900
                       transition disabled:opacity-50"
          >
            {isLoading && <Loader2 size={16} className="animate-spin" />}
            Run Command
          </button>
        </div>

      </div>
    </div>
  );
}
